import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, resolveAssetUrl } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { trackEvent } from "../../lib/analytics";
import { currency } from "../../lib/status";
import { Spinner } from "../../components/Brand";

const PAY_METHODS = [
    { value: "cash", label: "Cash on delivery" },
    { value: "wallet", label: "Wallet" },
    { value: "card", label: "Card (mock)" },
    { value: "online", label: "Online (mock)" },
];

export default function Cart() {
    const [cart, setCart] = useState(null);
    const [loading, setLoading] = useState(true);
    const [coupon, setCoupon] = useState("");
    const [payment, setPayment] = useState("cash");
    const [placing, setPlacing] = useState(false);
    const { user, refreshMe } = useAuth();
    const nav = useNavigate();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/cart");
                if (!cancelled) {
                    setCart(data);
                    setCoupon(data.coupon_code || "");
                }
            } catch (e) {
                if (!cancelled) toast.error(formatApiError(e.response?.data?.detail));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const reload = async () => {
        const { data } = await api.get("/cart");
        setCart(data);
    };

    const removeItem = async (itemId) => {
        try {
            const { data } = await api.delete(`/cart/items/${itemId}`);
            setCart(data);
            toast.success("Item removed");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const clearAll = async () => {
        if (!window.confirm("Empty the cart?")) return;
        try {
            const { data } = await api.delete("/cart");
            setCart(data);
            setCoupon("");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const applyCoupon = async () => {
        try {
            const { data } = await api.post("/cart/coupon", {
                code: coupon || null,
            });
            setCart(data);
            if (coupon) toast.success("Coupon applied");
            else toast.success("Coupon cleared");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const checkout = async () => {
        if (!cart || cart.item_count === 0) return;
        setPlacing(true);
        try {
            const { data } = await api.post("/cart/checkout", {
                payment_method: payment,
            });
            await refreshMe();
            if (data.failures && data.failures.length > 0) {
                toast.error(
                    `Some payments failed: ${data.failures[0].error || "Payment failed"}`,
                );
            } else {
                toast.success(
                    `${data.bookings.length} booking${
                        data.bookings.length > 1 ? "s" : ""
                    } placed`,
                );
                const total = (data.bookings || []).reduce((s, b) => s + (b.total_amount || 0), 0);
                trackEvent("booking_created", {
                    bookings: data.bookings.length,
                    value: total,
                    currency: "AED",
                    payment_method: payment,
                });
                if (payment !== "cash") {
                    trackEvent("payment_completed", { value: total, currency: "AED" });
                }
            }
            // Navigate to first booking detail or list
            if (data.bookings.length === 1) {
                nav(`/customer/bookings/${data.bookings[0].id}`);
            } else {
                nav("/customer/bookings");
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setPlacing(false);
        }
    };

    if (loading) return <Spinner />;
    if (!cart) return null;

    const walletBalance = user?.wallet_balance || 0;
    const walletShort =
        payment === "wallet" && walletBalance < cart.total;

    return (
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-7">
                <div className="lx-label text-[#B2912A] mb-2">Your cart</div>
                <h1 className="font-serif text-4xl text-[#180A21] mb-6">
                    Review &amp; checkout.
                </h1>

                {cart.item_count === 0 ? (
                    <div
                        className="border border-[#E5E2DC] p-12 text-center"
                        data-testid="cart-empty"
                    >
                        <div className="text-5xl mb-4 opacity-30">∅</div>
                        <p className="text-[#555] mb-6">Your cart is empty.</p>
                        <Link
                            to="/customer"
                            data-testid="cart-browse-link"
                            className="lx-btn-primary px-6 py-3 inline-block"
                        >
                            Browse services
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {cart.items.map((it) => (
                            <div
                                key={it.id}
                                data-testid={`cart-item-${it.id}`}
                                className="border border-[#E5E2DC] p-5 flex gap-5"
                            >
                                <div className="w-24 h-24 bg-[#F3F2F0] shrink-0 overflow-hidden">
                                    {it.service?.image_url && (
                                        <img
                                            src={resolveAssetUrl(it.service.image_url)}
                                            alt={it.service.name}
                                            className="w-full h-full object-cover"
                                        />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-serif text-lg text-[#180A21]">
                                        {it.service?.name}
                                    </div>
                                    <div className="text-xs text-[#555] mt-1">
                                        {new Date(it.scheduled_at).toLocaleString()} ·{" "}
                                        {it.service?.duration_minutes} min
                                    </div>
                                    <div className="text-xs text-[#555] mt-0.5 truncate">
                                        {it.address?.line1}, {it.address?.city}
                                    </div>
                                    {it.notes && (
                                        <div className="text-xs italic text-[#555] mt-1">
                                            {it.notes}
                                        </div>
                                    )}
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="font-serif text-xl text-[#180A21]">
                                        {currency(it.line_total, cart.currency)}
                                    </div>
                                    <button
                                        data-testid={`cart-remove-${it.id}`}
                                        onClick={() => removeItem(it.id)}
                                        className="text-xs text-red-600 hover:underline mt-2"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))}

                        <button
                            data-testid="cart-clear"
                            onClick={clearAll}
                            className="text-xs text-[#555] hover:text-red-600 mt-2"
                        >
                            Clear cart
                        </button>
                    </div>
                )}
            </div>

            {cart.item_count > 0 && (
                <aside className="lg:col-span-5">
                    <div className="lx-card p-7 lg:sticky lg:top-24 space-y-5">
                        <div className="lx-label text-[#B2912A]">Order summary</div>

                        <div>
                            <label className="lx-label text-[#555] mb-2 block">
                                Coupon code
                            </label>
                            <div className="flex gap-2">
                                <input
                                    data-testid="cart-coupon-input"
                                    value={coupon}
                                    onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                                    placeholder="WELCOME10"
                                    className="flex-1 px-3 py-2.5 border border-[#E5E2DC] focus:border-[#D4AF37] focus:outline-none text-sm"
                                />
                                <button
                                    data-testid="cart-coupon-apply"
                                    onClick={applyCoupon}
                                    className="px-4 py-2.5 border border-[#180A21] text-[#180A21] text-sm font-medium"
                                >
                                    {cart.coupon_code ? "Update" : "Apply"}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="lx-label text-[#555] mb-2 block">
                                Payment method
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {PAY_METHODS.map((m) => (
                                    <button
                                        key={m.value}
                                        data-testid={`cart-pay-${m.value}`}
                                        onClick={() => setPayment(m.value)}
                                        className={`py-2.5 text-xs border transition ${
                                            payment === m.value
                                                ? "bg-[#180A21] text-[#D4AF37] border-[#180A21]"
                                                : "border-[#E5E2DC] text-[#555] hover:border-[#D4AF37]/50"
                                        }`}
                                    >
                                        {m.label}
                                    </button>
                                ))}
                            </div>
                            {payment === "wallet" && (
                                <div
                                    className={`text-xs mt-2 ${
                                        walletShort ? "text-red-600" : "text-[#555]"
                                    }`}
                                    data-testid="cart-wallet-balance"
                                >
                                    Wallet balance: {currency(walletBalance, cart.currency)}{" "}
                                    {walletShort && "— insufficient"}
                                </div>
                            )}
                        </div>

                        <div className="pt-3 border-t border-[#F3F2F0] space-y-2 text-sm">
                            <Row label="Subtotal" value={currency(cart.subtotal, cart.currency)} />
                            {cart.membership_discount > 0 && (
                                <Row
                                    label="Member discount"
                                    value={`- ${currency(
                                        cart.membership_discount,
                                        cart.currency,
                                    )}`}
                                />
                            )}
                            {cart.coupon_discount > 0 && (
                                <Row
                                    label="Coupon"
                                    value={`- ${currency(cart.coupon_discount, cart.currency)}`}
                                />
                            )}
                            <Row label="Tax" value={currency(cart.tax, cart.currency)} />
                            <div className="border-t border-[#F3F2F0] pt-3 flex items-center justify-between">
                                <div className="lx-label text-[#180A21]">Total</div>
                                <div
                                    className="font-serif text-2xl text-[#180A21]"
                                    data-testid="cart-total"
                                >
                                    {currency(cart.total, cart.currency)}
                                </div>
                            </div>
                        </div>

                        <button
                            data-testid="cart-checkout-btn"
                            onClick={checkout}
                            disabled={placing || walletShort}
                            className="lx-btn-gold w-full py-3.5 font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
                        >
                            {placing ? <Spinner /> : `Place order — ${cart.item_count} item${cart.item_count > 1 ? "s" : ""}`}
                        </button>
                        <p className="text-xs text-[#555] text-center">
                            Each item becomes a separate booking.
                        </p>
                    </div>
                </aside>
            )}
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-[#555]">{label}</span>
            <span className="text-[#180A21] font-medium">{value}</span>
        </div>
    );
}
